export const formatShortDate = (
  value: string | Date | null | undefined
): string => {
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString();
};

export const formatLongDate = (
  value: string | Date | null | undefined
): string => {
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatTimestamp = (
  value: string | Date | null | undefined
): string => {
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  });
};

/**
 * Format a date string or Date object into a human-readable format
 * @param dateString - Date string or Date object
 * @returns Formatted date string
 */
export function formatDate(dateString: string | Date): string {
  try {
    // Check if the input is null or undefined
    if (!dateString) return "N/A";

    // Create a date object from the input
    const date = typeof dateString === "string" ? new Date(dateString) : dateString;

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return typeof dateString === "string" ? dateString : "Invalid Date";
    }

    // Format the date
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  } catch (error) {
    // Return the original string or a fallback message if formatting fails
    return typeof dateString === "string" ? dateString : "Error formatting date";
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago") for recent dates,
 * or as a short date for older dates
 * @param dateString - Date string or Date object
 * @returns Formatted relative time or short date string
 */
export function formatRelativeDate(
  dateString: string | Date | null | undefined
): string {
  if (!dateString) return "—";
  
  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    // Show relative time for dates within the last week
    if (diffInHours < 24) {
      return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
        -Math.floor(diffInHours),
        "hour"
      );
    } else if (diffInHours < 168) { // 7 days
      return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
        -Math.floor(diffInHours / 24),
        "day"
      );
    } else {
      // For older dates, show short date format
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(date);
    }
  } catch (error) {
    return typeof dateString === "string" ? dateString : "Error formatting date";
  }
}

/**
 * Check if a field name likely represents a date field
 * @param fieldName - The name of the field to check
 * @returns True if the field name suggests it contains date information
 */
export function isDateField(fieldName: string): boolean {
  return fieldName.toLowerCase().includes('at') ||
         fieldName.toLowerCase().includes('date') ||
         fieldName.toLowerCase().includes('time');
}
